/**
 * Inbound leg of the Jubit cross-domain hand-off.
 *
 * jubit.ai is the identity source of truth. When a visitor starts on jubuddy.com or
 * gghere.com and is sent to jubit.ai to sign in or register, the hub mints a one-time
 * exchange token and returns them to:
 *
 *   <origin>/auth/callback?sso_token=<token>&next=<path>
 *
 * (see jubit-ai-universe `src/lib/auth/crossDomainReturn.ts`).
 *
 * Until this module existed, `/auth/callback` understood only the PKCE `?code=` flow,
 * so `sso_token` fell straight through: the callback saw no session, gave up, and
 * navigated to its default destination — leaving the user on jubuddy.com still signed
 * OUT, having just successfully authenticated on jubit.ai. That silent drop is the one
 * defect that made the whole redirect path unusable for jubuddy and gghere.
 *
 * The token is single-use and short-lived (5 minutes, consumed atomically by the
 * `auth-jubit-sso` edge function), so it must be redeemed exactly once — see the
 * caller's guard against React's double-invoked effects.
 */

import { supabase } from '../lib/supabase.ts';

/** The Jubit project that owns auth. Same project this app's client points at. */
function jubitFunctionsBase(): string | null {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) return null;
  return `${url.replace(/\/+$/, '')}/functions/v1`;
}

export interface SsoExchangeResult {
  ok: boolean;
  error?: string;
}

/**
 * Redeem a one-time SSO exchange token for a real Supabase session.
 *
 * Returns `ok: false` with a human-readable reason rather than throwing: the caller
 * renders it, and a failed hand-off must still leave the user somewhere usable.
 */
export async function exchangeJubitSsoToken(token: string): Promise<SsoExchangeResult> {
  const base = jubitFunctionsBase();
  if (!base) return { ok: false, error: 'Sign-in is not configured for this environment.' };

  let payload: {
    success?: boolean;
    needs_reauth?: boolean;
    error?: string;
    session?: { access_token?: string; refresh_token?: string };
  };

  try {
    const response = await fetch(`${base}/auth-jubit-sso`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '',
      },
      body: JSON.stringify({ action: 'validate_token', exchange_token: token }),
    });
    payload = await response.json();

    if (!response.ok || !payload?.success) {
      return {
        ok: false,
        error: payload?.error || 'This sign-in link is no longer valid. Please try again.',
      };
    }
  } catch {
    return { ok: false, error: 'Could not reach the sign-in service. Please try again.' };
  }

  // The hub answers 200 with `needs_reauth` when it could not mint session tokens
  // (e.g. the account has no usable email). Treat that as a failure rather than
  // reporting success with no session.
  const access_token = payload.session?.access_token;
  const refresh_token = payload.session?.refresh_token;
  if (payload.needs_reauth || !access_token || !refresh_token) {
    return { ok: false, error: 'Session could not be created — please sign in again.' };
  }

  const { error } = await supabase.auth.setSession({ access_token, refresh_token });
  if (error) return { ok: false, error: error.message };

  return { ok: true };
}
